import * as z from "@/lib/zod";
import { normalizeSportsbookId } from "@/lib/sportsbook-registry";
import { US_STATES, type UsState } from "@/lib/us-states";

export const BOOK_REGULATIONS = ["licensed", "federal", "dfs", "sweepstakes", "offshore"] as const;
const BOOK_MARKET_KINDS = ["retail", "exchange", "dfs", "prediction"] as const;

export type BookRegulation = (typeof BOOK_REGULATIONS)[number];
export type BookMarketKind = (typeof BOOK_MARKET_KINDS)[number];

export const BOOK_GROUP_LABEL: Readonly<Record<BookRegulation, string>> = {
  licensed: "Sportsbooks",
  federal: "Prediction markets",
  dfs: "DFS & pick'em",
  sweepstakes: "Sweepstakes",
  offshore: "Offshore",
};

export type BookFacts = {
  readonly id: string;
  readonly kind: BookMarketKind;
  readonly regulation: BookRegulation;
  readonly states: readonly UsState[];
};

export type BookCatalog = Readonly<Record<string, BookFacts>>;

export type BookGroup<T extends string = string> = {
  readonly regulation: BookRegulation | null;
  readonly label: string;
  readonly books: readonly T[];
};

export const usStateSchema = z.custom<UsState>((value) => typeof value === "string" && Object.hasOwn(US_STATES, value));

const BookRow = z.object({
  book_id: z.string().min(1),
  kind: z.enum(BOOK_MARKET_KINDS),
  regulation: z.enum(BOOK_REGULATIONS),
  states: z.array(usStateSchema),
});
const BookRowId = z.object({ book_id: z.string() });
const BooksResponse = z.object({ data: z.array(z.unknown()) });

export type ParsedBookCatalog = { readonly catalog: BookCatalog | null; readonly dropped: readonly string[] };

function catalogId(id: string): string {
  return normalizeSportsbookId(id) || id;
}

export function parseBookCatalog(body: unknown): ParsedBookCatalog {
  const catalog: Record<string, BookFacts> = {};
  const dropped: string[] = [];
  for (const raw of BooksResponse.parse(body).data) {
    const row = BookRow.safeParse(raw);
    if (!row.success) {
      dropped.push(BookRowId.safeParse(raw).data?.book_id ?? "(no book_id)");
      continue;
    }
    const id = catalogId(row.data.book_id);
    catalog[id] = { id, kind: row.data.kind, regulation: row.data.regulation, states: row.data.states };
  }
  return { catalog: Object.keys(catalog).length === 0 ? null : catalog, dropped };
}

export function bookFacts(catalog: BookCatalog, id: string): BookFacts | undefined {
  const key = catalogId(id);
  return Object.hasOwn(catalog, key) ? catalog[key] : undefined;
}

export function groupBooks<T extends string>(catalog: BookCatalog | null, ids: readonly T[]): readonly BookGroup<T>[] {
  const regulationOf = (id: T) => (catalog === null ? undefined : bookFacts(catalog, id)?.regulation);
  const groups: BookGroup<T>[] = BOOK_REGULATIONS.map((regulation) => ({ regulation, label: BOOK_GROUP_LABEL[regulation], books: ids.filter((id) => regulationOf(id) === regulation) }));
  groups.push({ regulation: null, label: "Other books", books: ids.filter((id) => regulationOf(id) === undefined) });
  return groups.filter((group) => group.books.length > 0);
}

export function hasNamedGroups(groups: readonly BookGroup[]): boolean {
  return groups.some((group) => group.regulation !== null);
}
